import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { InfiniteQueryResult, QueryKey } from "react-query";
import { useTranslation } from "react-i18next";
import { AgGridReactProps } from "@ag-grid-community/react";
import { GridApi, RowNode } from "@ag-grid-community/core";

import {
  DataGridFilters,
  DataGridOptions,
} from "fe-shared/src/components/DataGrid/modules/types";
import useUpdateEffect from "fe-shared/src/hooks/useUpdateEffect";
import {
  DataGrid,
  ExportExcelData,
  showNotification,
} from "fe-shared/src/components";
import { CloseIcon } from "fe-shared/src/components/Icons";

import {
  GridInventoryAdjustment,
  GridInventoryAdjustmentList,
  InventoryAdjustmentStatusCode,
} from "../../types/inventoryAdjustments";
import { ENTITY } from "../AssigneeBulkSelect/AssigneeBulkSelect";
import UserContext from "../../contexts/userContext";
import useInventoryAdjustmentsExport from "../../hooks/useInventoryAdjustmentsExport";
import useAssignInventoryAdjustments from "../../hooks/useAssignInventoryAdjustments";
import {
  getFlattenedInventoryAdjustments,
  getRequestParams,
} from "../../helpers/inventoryAdjustmentsRequestConverter";
import AssigneeBulkSelect from "../AssigneeBulkSelect";

import {
  BULK_ASSIGN_STATUSES,
  getColumnDefs,
  getRenderers,
} from "./modules/config";
import InventoryAdjustmentAdditionalFilters from "./components/InventoryAdjustmentAdditionalFilters";

import styles from "./InventoryAdjustmentsDataGrid.module.scss";

interface Props extends AgGridReactProps {
  inventoryAdjustments: InfiniteQueryResult<
    GridInventoryAdjustmentList<GridInventoryAdjustment>
  >;
  lastOpenedNumber: string | null;
  clearLastOpenedNumber: () => void;
  options: DataGridOptions;
  filters: DataGridFilters;
  setOptions: (options: DataGridOptions) => void;
  setFilters: (filters: DataGridFilters) => void;
  canExport?: boolean;
  canAssign?: boolean;
  listQueryKey: QueryKey;
}

const InventoryAdjustmentsDataGrid = ({
  inventoryAdjustments,
  lastOpenedNumber,
  clearLastOpenedNumber,
  filters,
  canExport,
  canAssign = false,
  listQueryKey,
  ...otherProps
}: Props) => {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const gridApiRef = useRef<GridApi | null>(null);
  const columnDefs = useMemo(() => getColumnDefs(t), [t]);
  const userName = `${user?.firstName} ${user?.lastName}`;
  const [selectedRows, setSelectedRows] = useState<GridInventoryAdjustment[]>(
    []
  );
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [changeAssignee] = useAssignInventoryAdjustments();

  const assignGridApi = (api: GridApi) => {
    gridApiRef.current = api;
  };

  const renderers = useMemo(() => getRenderers(canAssign), [canAssign]);

  const onFirstDataRendered = useCallback(() => {
    if (lastOpenedNumber) {
      gridApiRef.current!.ensureNodeVisible(
        (node: RowNode) => node.id === lastOpenedNumber.toString(),
        "middle"
      );

      clearLastOpenedNumber();
    }
  }, [lastOpenedNumber, clearLastOpenedNumber]);

  const rowCount = inventoryAdjustments.data?.[0].total || 0;
  const rowData = useMemo(
    () => getFlattenedInventoryAdjustments(inventoryAdjustments.data),
    [inventoryAdjustments.data]
  );

  const requestParams = getRequestParams(filters, otherProps.options);
  const { columnConfig, getDataRows } = useInventoryAdjustmentsExport(
    requestParams
  );

  const updateFilters = (filtersUpdate: DataGridFilters) => {
    gridApiRef.current?.setFilterModel({
      ...filters,
      ...filtersUpdate,
    });
  };

  const onSelfAssignSuccess = () => {
    updateFilters({
      assigneeDisplayName: {
        type: "textFilter",
        value: userName,
      },
      statusCode: {
        type: "selectFilter",
        value: [InventoryAdjustmentStatusCode.InReview],
      },
    });
  };

  const getIdsForBulkAssign = async () => {
    return Promise.resolve(selectedRows.map((tr) => tr.overallAdjustmentId));
  };

  const onExportError = (error: any) => {
    showNotification(
      error.response?.data.message || error.message,
      undefined,
      CloseIcon
    );
  };

  const isAssignable = useMemo(
    () =>
      selectedRows.length > 1 &&
      canAssign &&
      (isAllSelected && selectedRows.length < rowCount
        ? BULK_ASSIGN_STATUSES.includes(
            filters.statusCode?.value as InventoryAdjustmentStatusCode
          )
        : selectedRows.every((item) =>
            BULK_ASSIGN_STATUSES.includes(item.statusCode)
          )),
    [
      filters.statusCode?.value,
      isAllSelected,
      rowCount,
      selectedRows,
      canAssign,
    ]
  );

  useUpdateEffect(() => {
    if (!inventoryAdjustments.isFetchingMore && isAllSelected) {
      gridApiRef.current?.selectAll();
    }
  }, [inventoryAdjustments.isFetchingMore]);

  return (
    <main className={styles.container}>
      <div className={styles.headerBlock}>
        <h1 className={styles.header}>
          {t("inventoryAdjustment.grid.title")}
          {!!rowCount && <span className={styles.totalCount}>{rowCount}</span>}
        </h1>
        <div className={styles.buttons}>
          <AssigneeBulkSelect
            isAssignable={isAssignable}
            getIds={getIdsForBulkAssign}
            onSelfAssignSuccess={onSelfAssignSuccess}
            changeAssignee={changeAssignee}
            entity={ENTITY.InventoryAdjustment}
          />
          {canExport && (
            <ExportExcelData
              dataRows={[]}
              getDataRows={getDataRows}
              columnConfig={columnConfig}
              onError={onExportError}
              buttonId="export-inventory-adjustments-button"
              fileName={t("inventoryAdjustment.grid.export.fileName")}
              buttonLabel={t("inventoryAdjustment.grid.export.buttonLabel")}
            />
          )}
          <InventoryAdjustmentAdditionalFilters
            filters={filters}
            updateFilters={updateFilters}
          />
        </div>
      </div>
      <DataGrid
        idField="overallAdjustmentNumber"
        rowSelection="multiple"
        suppressRowClickSelection
        suppressCellSelection
        assignGridApi={assignGridApi}
        onFirstDataRendered={onFirstDataRendered}
        columnDefs={columnDefs}
        rowData={rowData}
        isLoading={inventoryAdjustments.isLoading}
        fetchMore={inventoryAdjustments.fetchMore}
        canFetchMore={inventoryAdjustments.canFetchMore}
        isFetchingMore={inventoryAdjustments.isFetchingMore}
        className={styles.adjustmentsDataGrid}
        filters={filters}
        frameworkComponents={renderers}
        onSelectionChanged={(e) => {
          const rows = e.api.getSelectedRows();

          setSelectedRows(rows);
          setIsAllSelected(!!rows.length && rows.length === rowData?.length);
        }}
        {...otherProps}
      />
    </main>
  );
};

export default InventoryAdjustmentsDataGrid;
